import React from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AIGenerateButtonProps {
    onClick: () => void;
    isLoading?: boolean;
    disabled?: boolean;
    label?: string;
    loadingLabel?: string;
    variant?: 'default' | 'outline' | 'ghost' | 'secondary';
    size?: 'sm' | 'default' | 'lg';
    className?: string;
    fullWidth?: boolean;
}

export const AIGenerateButton: React.FC<AIGenerateButtonProps> = ({
    onClick,
    isLoading = false,
    disabled = false,
    label = 'Gerar com IA',
    loadingLabel = 'Gerando...',
    variant = 'outline',
    size = 'sm',
    className,
    fullWidth = false,
}) => {
    return (
        <Button
            type="button"
            variant={variant}
            size={size}
            onClick={onClick}
            disabled={isLoading || disabled}
            className={cn(
                'gap-2 transition-all duration-300',
                'bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-fuchsia-500/10',
                'hover:from-violet-500/20 hover:via-purple-500/20 hover:to-fuchsia-500/20',
                'border-violet-500/30 hover:border-violet-500/50',
                'text-violet-400 hover:text-violet-300',
                isLoading && 'animate-pulse',
                fullWidth && 'w-full',
                className
            )}
        >
            {isLoading ? (
                <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{loadingLabel}</span>
                </>
            ) : (
                <>
                    <Sparkles className="w-4 h-4" />
                    <span>{label}</span>
                </>
            )}
        </Button>
    );
};

// Larger version for prominent placement
export const AIGenerateButtonLarge: React.FC<AIGenerateButtonProps> = (props) => {
    return (
        <AIGenerateButton
            {...props}
            size="default"
            className={cn(
                'h-12 px-6 text-base font-medium',
                'shadow-lg shadow-violet-500/20',
                props.className
            )}
        />
    );
};

// Inline version for form fields
export const AIGenerateButtonInline: React.FC<AIGenerateButtonProps> = (props) => {
    return (
        <AIGenerateButton
            {...props}
            size="sm"
            label={props.label || '✨ IA'}
            loadingLabel="..."
            className={cn(
                'h-8 px-3 text-xs',
                props.className
            )}
        />
    );
};
